/* eslint-disable css-modules/no-unused-class, no-nested-ternary */

import { FC, useState, useRef, useEffect } from 'react';
import { translate } from 'translations';
import { Icon } from 'learning-panel-components';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { selectIsLeanUser, selectRole } from '@redux/slices/userSlice';
import { getPlatformUrl, mixpanelTrack, gainsightTrack } from 'utils';
import { KeyboardArrowDownIcon } from '../../../uiLibraryImports';
import styles from './userSection.module.scss';

const UserSection: FC<UserSectionProps> = (props: UserSectionProps) => {
    const { imageSrc, userName, onLogout, setIsAccountSettingsOpen, setIsContactSupportOpen, narrowScreen } = props;

    const [showDropDown, setShowDropDown] = useState<boolean>(false);

    const role = useSelector(selectRole);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const isLeanUser = useSelector(selectIsLeanUser);
    const isAdmin = role === 'admin';
    const isManager = role === 'manager';
    const isSuperAdmin = role === 'superAdmin';

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (showDropDown && ref.current && !ref.current.contains(e.target)) {
                setShowDropDown(false);
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [showDropDown]);

    const openContactForm = () => {
        setIsContactSupportOpen(true);
        mixpanelTrack({
            eventName: 'Contact Form Opened',
            properties: {
                'Form Trigger': 'Home'
            }
        });
        gainsightTrack('Contact Form Opened', { 'Form Trigger': 'Home' });
    };

    const logout = (e: any) => {
        e.stopPropagation();
        onLogout();
    };

    return (
        <div className={styles['user-wrapper']}>
            <img src={imageSrc} className={styles.image} alt="profile" />
            <div className={styles['user-name-wrapper']} onClick={() => setShowDropDown(!showDropDown)} ref={ref}>
                {!narrowScreen && <div>{userName}</div>}
                <KeyboardArrowDownIcon className={styles['arrow-icon']} />
                {showDropDown && (
                    <div
                        className={classNames(styles['user-actions-wrapper'], {
                            [styles['user-actions-wrapper-with-admin']]: isAdmin || isManager || isSuperAdmin,
                            [styles['user-actions-wrapper-lean']]: isLeanUser
                        })}
                    >
                        <div className={styles['action-wrapper']} onClick={() => setIsAccountSettingsOpen(true)}>
                            <Icon name="account" className={styles.icon} />
                            <div className={styles.text}>{translate('AccountSettings')}</div>
                        </div>
                        {(isAdmin || isManager || isLeanUser) && (
                            <div className={styles['action-wrapper']} onClick={() => openContactForm()}>
                                <Icon name="support" className={styles.icon} />
                                <div className={styles.text}>
                                    {isLeanUser ? translate('ContactUs') : translate('ContactSupport')}
                                </div>
                            </div>
                        )}
                        {(isAdmin || isManager || isSuperAdmin) && (
                            <a
                                href={isAdmin ? `${getPlatformUrl()}/dashboard` : `${getPlatformUrl()}/admin/dashboard`}
                                className={styles['action-wrapper']}
                            >
                                <Icon name="adminPanel" className={styles.icon} />
                                <div className={styles.text}>{translate('Switch to admin')}</div>
                            </a>
                        )}
                        <div className={styles['action-wrapper']} onClick={e => logout(e)}>
                            <Icon name="logout" className={styles.icon} />
                            <div className={styles.text}>{translate('Logout')}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface UserSectionProps {
    imageSrc: string;
    userName: string;
    onLogout: Function;
    setIsAccountSettingsOpen: Function;
    setIsContactSupportOpen: Function;
    narrowScreen: boolean;
}

export default UserSection;
